import React, { useState } from "react";
import {
  Button,
  TextField,
  Typography,
  FormControl,
  Autocomplete,
  ListItemText,
} from "@mui/material";
import { checkIsCurrentUserOfClinic } from "utils/currentUser.util";
import { REPORT_NOTE_AUTOMATED_NOTES } from "static/constants";

const ReportNoteSectionInputForm = ({
  formikProps,
  onCloseModal,
  accounts,
}) => {
  const {
    values,
    errors,
    touched,
    resetForm,
    isSubmitting,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
  } = formikProps;

  const handleClose = () => {
    resetForm();
    onCloseModal();
  };

  const [inputValue, setInputValue] = useState("");

  const handleKeyDown = (event) => {
    if (
      !inputValue &&
      event.key === "Backspace" &&
      values.placeholders?.length
    ) {
      const newPlaceholders = [...values.placeholders];
      const lastPlaceholder = newPlaceholders.pop();
      setFieldValue("placeholders", newPlaceholders);
      setInputValue(lastPlaceholder);
    }
  };

  const isClinicUser = checkIsCurrentUserOfClinic();

  return (
    <form style={{ minWidth: "500px" }}>
      <div style={{ margin: "2% 0" }}>
        <FormControl style={{ width: "100%" }}>
          <TextField
            id="note"
            name="note"
            label="Note"
            variant="outlined"
            required
            error={touched.note && !!errors.note}
            helperText={touched.note && errors.note}
            style={{ width: "100%" }}
            defaultValue={values?.note}
            onChange={(e) => {
              setFieldTouched("note");
              setFieldValue("note", e.target.value);
            }}
          />
        </FormControl>
      </div>
      <div style={{ margin: "2% 0" }}>
        <FormControl fullWidth style={{ width: "100%" }}>
          <Autocomplete
            id="associated_note"
            name="associated_note"
            value={values.associated_note}
            options={Object.keys(REPORT_NOTE_AUTOMATED_NOTES)}
            onChange={(e, value) => {
              setFieldTouched("associated_note");
              const newValue = Object.keys(
                REPORT_NOTE_AUTOMATED_NOTES
              ).includes(value)
                ? value
                : null;
              setFieldValue("associated_note", newValue);
            }}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps} data-action={`select-${option}`}>
                  <ListItemText
                    primary={option}
                    secondary={REPORT_NOTE_AUTOMATED_NOTES[option]}
                    primaryTypographyProps={{
                      style: { whiteSpace: "normal", fontWeight: "bold" },
                    }}
                    secondaryTypographyProps={{
                      style: { whiteSpace: "normal" },
                    }}
                  />
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Associated Note"
                variant="outlined"
                type="text"
                inputProps={{ ...params.inputProps, readOnly: true }}
                required={isClinicUser}
                error={touched.associated_note && !!errors.associated_note}
                helperText={touched.associated_note && errors.associated_note}
              />
            )}
          />
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mt: 0.5, ml: 1 }}
          >
            This corresponding title will help to show automate notes in Report
            note section
          </Typography>
        </FormControl>
      </div>

      <div style={{ margin: "2% 0" }}>
        <FormControl fullWidth style={{ width: "100%" }}>
          <Autocomplete
            id="account"
            name="account"
            value={values.account}
            options={accounts?.map((account) => account?.name) || []}
            onChange={(e, value) => {
              setFieldTouched("account");
              setFieldValue("account", value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Account"
                variant="outlined"
                type="text"
                required={isClinicUser}
                error={touched.account && !!errors.account}
                helperText={touched.account && errors.account}
              />
            )}
          />
        </FormControl>
      </div>
      <div style={{ margin: "2% 0" }}>
        <FormControl
          fullWidth
          variant="outlined"
          style={{ width: "100%" }}
          error={
            (touched.placeholders && errors.placeholders) ||
            inputValue?.length > 150
          }
        >
          <Autocomplete
            multiple
            freeSolo
            autoSelect
            id="placeholders"
            name="placeholders"
            openOnFocus={true}
            value={values.placeholders || []}
            options={[]}
            inputValue={inputValue}
            onInputChange={(e, newInputValue) => {
              setInputValue(newInputValue);
            }}
            onKeyDown={handleKeyDown}
            onChange={(e, newValue) => {
              if (e.target.value && e.target.value?.length > 150) {
                setInputValue(e.target.value);
                return;
              }

              const newPlaceholders = newValue
                ?.map((value) => value?.trim())
                ?.filter(Boolean);

              setFieldTouched("placeholders");
              setFieldValue("placeholders", newPlaceholders);
              setInputValue("");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Placeholders"
                required
                placeholder="Type and press enter to add placeholders"
                error={
                  (touched.placeholders && errors.placeholders) ||
                  inputValue?.length > 150
                }
                helperText={
                  (touched.placeholders && errors.placeholders) ||
                  (inputValue?.length > 150 &&
                    "Placeholder can have maximum 150 characters.")
                }
              />
            )}
          />
        </FormControl>
      </div>
      <div
        style={{
          display: "inline-flex",
          justifyContent: "center",
          float: "right",
          gap: "10px",
        }}
      >
        <Button
          variant="outlined"
          color="secondary"
          disabled={isSubmitting}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          type="button"
          color="secondary"
          variant="contained"
          onClick={isSubmitting ? null : handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </div>
    </form>
  );
};

export default ReportNoteSectionInputForm;
